import { useState } from 'react';
import { Copy, Loader2, X } from 'lucide-react';
import { Button, Input } from '@lms/ui';
import type { Program } from '@lms/shared/types';
import { useDuplicateProgram } from '../hooks/usePrograms';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from './Bilingual';
import { copy } from '../copy/copy';

interface DuplicateProgramModalProps {
  program: Pick<Program, 'id' | 'code' | 'name'>;
  onClose: () => void;
  onDuplicated: (program: Program) => void;
}

export function DuplicateProgramModal({
  program,
  onClose,
  onDuplicated,
}: DuplicateProgramModalProps) {
  const { t } = useLanguage();
  const duplicateProgramMutation = useDuplicateProgram();
  const [name, setName] = useState(`${program.name} (Salinan)`);
  const [code, setCode] = useState(`${program.code}-COPY`);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');
    duplicateProgramMutation.mutate(
      { id: program.id, code: code.trim(), name: name.trim() },
      {
        onSuccess: onDuplicated,
        onError: error => {
          setErrorMessage(
            error instanceof Error ? error.message : t(copy.programDuplicate.errorFallback)
          );
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/40 p-4 backdrop-blur-sm animate-fadeIn sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="duplicate-program-title"
        className="w-full max-w-xl rounded-[2rem] bg-surface-container-lowest shadow-level4 animate-slideUp"
      >
        <header className="flex items-start justify-between gap-4 bg-surface-container-low px-5 py-5 sm:px-8 sm:py-6">
          <div>
            <p className="text-[10px] font-label font-bold uppercase tracking-widest text-primary">
              <Bilingual {...copy.programDuplicate.eyebrow} />
            </p>
            <h2
              id="duplicate-program-title"
              className="mt-1 text-2xl font-headline font-bold tracking-tight text-on-surface"
            >
              <Bilingual {...copy.programDuplicate.title} />
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t(copy.common.close)}
            className="flex size-10 shrink-0 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <X size={20} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="space-y-6 px-5 py-6 sm:px-8 sm:py-8">
          <div className="rounded-2xl bg-surface-container-low p-5">
            <p className="text-sm font-body leading-relaxed text-on-surface-variant">
              <Bilingual {...copy.programDuplicate.description} />
            </p>
            <p className="mt-3 text-xs font-body font-medium leading-relaxed text-on-surface-variant/75">
              <Bilingual {...copy.programDuplicate.exclusions} />
            </p>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="duplicate-program-name"
              className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant/60"
            >
              <Bilingual {...copy.programDuplicate.labelName} />
            </label>
            <Input
              id="duplicate-program-name"
              value={name}
              onChange={event => setName(event.target.value)}
              required
              className="h-12 border-none bg-surface-container-low focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="duplicate-program-code"
              className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant/60"
            >
              <Bilingual {...copy.programDuplicate.labelCode} />
            </label>
            <Input
              id="duplicate-program-code"
              value={code}
              onChange={event => setCode(event.target.value)}
              required
              className="h-12 border-none bg-surface-container-low focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {errorMessage ? (
            <p
              role="alert"
              className="rounded-xl bg-error/10 px-4 py-3 text-sm font-body text-error"
            >
              {errorMessage}
            </p>
          ) : null}

          <footer className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="h-11 rounded-xl px-4 text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant transition-colors hover:bg-surface-container-low focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Bilingual {...copy.programDuplicate.cancel} />
            </button>
            <Button
              type="submit"
              disabled={duplicateProgramMutation.isPending}
              className="h-11 rounded-xl px-5 text-[10px] font-label font-bold uppercase tracking-widest"
            >
              {duplicateProgramMutation.isPending ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Copy size={16} />
              )}
              <Bilingual {...copy.programDuplicate.submit} />
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
